import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { Upload, CheckCircle2, Loader2, ChevronRight, RefreshCw, Trash2, RotateCcw } from 'lucide-react';
import { processPaymentsFileDetailed } from '@/lib/fileProcessors/paymentsProcessor';
import { getCompanySalesTaxRate } from '@/data/supabase/companies';
import { processPayoutsFile, mapPayoutToSalesTransaction } from '@/lib/fileProcessors/payoutsProcessor';
import { processQuantitiesFile, extractPOSItemIdentifiers } from '@/lib/fileProcessors/quantitiesProcessor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { resetCurrentUserPOSTrainingData } from '@/lib/posDataReset';
import { PLATFORMS, normalizePlatform, platformDisplayName } from '@/lib/platformConstants';
import toastLogo from '@/assets/toast-logo.png';
import doordashLogo from '@/assets/doordash-logo.png';
import uberEatsLogo from '@/assets/uber-eats-logo.svg';

interface POSReportsStep2CombinedProps {
  onNext: () => void;
  onPrevious?: () => void;
}

interface Platform {
  id: string;
  name: string;
  logo: string;
  status: string;
  salesUploaded: boolean;
  payoutsUploaded: boolean;
  quantitiesUploaded: boolean;
}

const PLATFORM_LOGOS: Record<string, string> = {
  toast: toastLogo,
  doordash: doordashLogo,
  ubereats: uberEatsLogo,
};

export const POSReportsStep2Combined = ({ onNext, onPrevious }: POSReportsStep2CombinedProps) => {
  const { toast } = useToast();
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<{ platformId: string; reportType: string } | null>(null);
  const [removing, setRemoving] = useState<{ platformId: string; reportType: string } | null>(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState<string[]>([]);
  const [resetting, setResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);

  useEffect(() => {
    loadPlatforms();
  }, []);

  const loadPlatforms = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setLoading(false);
        return;
      }

      // Get active POS integrations
      const { data: integrations, error } = await supabase
        .from('company_integrations')
        .select('*')
        .eq('company_id', companyId)
        .in('integration_type', [PLATFORMS.TOAST, PLATFORMS.DOORDASH, 'ubereats'])
        .eq('is_active', true);

      if (error) throw error;

      // Get upload status for each platform
      const platformsWithStatus = await Promise.all(
        (integrations || []).map(async (integration: any) => {
          const logo = PLATFORM_LOGOS[integration.integration_type];
          if (!logo) return null;

          // Check training progress
          const { data: progress } = await supabase
            .from('pos_training_progress')
            .select('status')
            .eq('company_id', companyId)
            .eq('platform', integration.integration_type)
            .single();

          // Check for existing report uploads
          const { data: reports } = await supabase
            .from('pos_report_uploads')
            .select('report_type')
            .eq('company_id', companyId)
            .eq('platform', integration.integration_type)
            .eq('status', 'completed');

          const payoutsUploadedDirectly = reports?.some(r => r.report_type === 'payouts') || false;
          const salesUploadedDirectly = reports?.some(r => r.report_type === 'sales') || false;

          // Backward compat: for delivery platforms, the old combined "Sales and Payout"
          // upload was stored as report_type='payouts'. If sales wasn't uploaded separately
          // but payouts was, count that as the sales upload being done.
          const isDelivery =
            normalizePlatform(integration.integration_type) === PLATFORMS.UBER ||
            normalizePlatform(integration.integration_type) === PLATFORMS.DOORDASH;
          const salesUploaded = salesUploadedDirectly || (isDelivery && payoutsUploadedDirectly);

          return {
            id: integration.integration_type,
            name: platformDisplayName(integration.integration_type),
            logo,
            status: progress?.status || 'not_started',
            salesUploaded,
            payoutsUploaded: payoutsUploadedDirectly,
            quantitiesUploaded: reports?.some(r => r.report_type === 'item_quantities') || false,
          };
        })
      );

      const validPlatforms = platformsWithStatus.filter(Boolean) as Platform[];
      setPlatforms(validPlatforms);
      
      // Auto-expand platforms that are not complete
      const incompletePlatforms = validPlatforms
        .filter(p => !p.salesUploaded || !p.payoutsUploaded || !p.quantitiesUploaded)
        .map(p => p.id);
      
      if (incompletePlatforms.length > 0) {
        setExpandedPlatforms([incompletePlatforms[0]]); // Expand first incomplete platform
      }
    } catch (error: any) {
      console.error('Error loading platforms:', error);
      toast({
        title: 'Error',
        description: 'Failed to load POS platforms',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (platformId: string, reportType: 'sales' | 'payouts' | 'item_quantities', file: File) => {
    try {
      setUploading({ platformId, reportType });
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      // Upload file to storage
      const filePath = `pos-reports/${companyId}/${platformId}/${reportType}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('company-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Process file based on type
      let processedData: any = null;

      if (reportType === 'sales') {
        const taxRate = await getCompanySalesTaxRate(companyId);
        const data = await processPaymentsFileDetailed(file, taxRate);
        processedData = data;
        if (!data || data.length === 0) {
          toast({
            title: 'No Data',
            description: 'The sales transactions file appears to be empty or could not be parsed.',
            variant: 'destructive',
          });
          setUploading(null);
          return;
        }
      } else if (reportType === 'payouts') {
        const data = await processPayoutsFile(file);
        processedData = data;
      } else if (reportType === 'item_quantities') {
        const data = await processQuantitiesFile(file);
        console.log(`Item quantities processed: ${data.length} items parsed`);
        console.log(`Sample parsed data:`, data.slice(0, 3).map(d => ({ 
          item_name: d.item_name, 
          quantity_sold: d.quantity_sold, 
          revenue: d.revenue 
        })));
        
        const identifiers = await extractPOSItemIdentifiers(file);
        console.log(`Item identifiers extracted: ${identifiers.length} unique items`);
        console.log(`Sample identifiers:`, identifiers.slice(0, 3).map(i => ({ 
          name: i.name, 
          quantity: i.quantity, 
          revenue: i.revenue 
        })));
        
        // Store as object with both raw data and extracted identifiers
        processedData = {
          raw_data: data,
          item_identifiers: identifiers,
        };
        
        // Verify data structure
        const totalQuantity = data.reduce((sum, item) => sum + (item.quantity_sold || 0), 0);
        const identifiersWithQuantity = identifiers.filter(i => (i.quantity || 0) > 0).length;
        console.log(`Data verification: total quantity in raw_data: ${totalQuantity}, identifiers with quantity: ${identifiersWithQuantity}/${identifiers.length}`);
        
        if (identifiers.length === 0) {
          toast({
            title: 'Warning',
            description: 'No items were found in the uploaded file. Please check that you uploaded the correct Item Quantities report.',
            variant: 'destructive',
          });
        }
      }

      // Save report upload record
      const { error: saveError } = await (supabase as any)
        .from('pos_report_uploads')
        .insert({
          company_id: companyId,
          platform: platformId,
          report_type: reportType,
          file_url: filePath,
          file_name: file.name,
          status: 'completed',
          processed_data: processedData,
        });

      if (saveError) throw saveError;

      // Insert sales transactions into DB so they appear in Sales Insights / dashboard
      if (reportType === 'sales' && Array.isArray(processedData) && processedData.length > 0) {
        const salesRows = processedData.map((row: any) => {
          const { payout_time, ...rest } = row;
          return { ...rest, company_id: companyId, platform: platformId };
        });
        const { error: insertSalesError } = await supabase
          .from('sales_transactions')
          .upsert(salesRows, { onConflict: 'company_id,platform,order_date,order_time,shop_id', ignoreDuplicates: false });
        if (insertSalesError) throw insertSalesError;
      }

      // Insert payouts into sales_transactions only if per-order sales data doesn't already exist
      if (reportType === 'payouts' && Array.isArray(processedData) && processedData.length > 0) {
        const dates = processedData.map((p: any) => p.sale_date || p.order_date || p.payout_date).filter(Boolean);
        const minDate = dates.sort()[0];
        const maxDate = dates.sort().pop();

        const { count: existingSalesCount } = await supabase
          .from('sales_transactions')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('platform', platformId)
          .gte('order_date', minDate)
          .lte('order_date', maxDate);

        if (existingSalesCount && existingSalesCount > 0) {
          console.log(`Skipping payout insert: ${existingSalesCount} per-order sales rows already exist for ${platformId} ${minDate}-${maxDate}`);
        } else {
          const salesRows = processedData.map((p: any) => ({
            ...mapPayoutToSalesTransaction(p),
            company_id: companyId,
          }));
          const { error: insertSalesError } = await supabase
            .from('sales_transactions')
            .upsert(salesRows, { onConflict: 'company_id,platform,order_date,order_time,shop_id', ignoreDuplicates: false });
          if (insertSalesError) {
            console.warn('Failed to upsert payouts into sales_transactions:', insertSalesError);
          }
        }
      }

      // Update training progress
      const updateField = reportType === 'sales' ? 'sales_report_uploaded' :
                          reportType === 'payouts' ? 'payouts_report_uploaded' :
                          'quantities_report_uploaded';

      await supabase
        .from('pos_training_progress' as any)
        .upsert({
          company_id: companyId,
          platform: platformId,
          [updateField]: true,
          status: 'uploading',
        }, {
          onConflict: 'company_id,platform',
        });

      // Update local state
      setPlatforms(prev => prev.map(p => {
        if (p.id === platformId) {
          return {
            ...p,
            salesUploaded: reportType === 'sales' ? true : p.salesUploaded,
            payoutsUploaded: reportType === 'payouts' ? true : p.payoutsUploaded,
            quantitiesUploaded: reportType === 'item_quantities' ? true : p.quantitiesUploaded,
          };
        }
        return p;
      }));

      toast({
        title: 'Success',
        description: `${reportType === 'item_quantities' ? 'Item quantities' : reportType.charAt(0).toUpperCase() + reportType.slice(1)} report uploaded and processed`,
      });
    } catch (error: any) {
      console.error('Error uploading report:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to upload report',
        variant: 'destructive',
      });
    } finally {
      setUploading(null);
    }
  };

  const handleRemoveReport = async (platformId: string, reportType: 'sales' | 'payouts' | 'item_quantities') => {
    try {
      setRemoving({ platformId, reportType });
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      // Get the report to find the file URL
      const { data: reports } = await supabase
        .from('pos_report_uploads')
        .select('id, file_url')
        .eq('company_id', companyId)
        .eq('platform', platformId)
        .eq('report_type', reportType)
        .eq('status', 'completed');

      if (reports && reports.length > 0) {
        // Delete file from storage (ignore errors as file might not exist)
        for (const report of reports) {
          if (report.file_url) {
            await supabase.storage
              .from('company-documents')
              .remove([report.file_url]);
          }
        }

        // Delete the report records
        const { error: deleteError } = await supabase
          .from('pos_report_uploads')
          .delete()
          .eq('company_id', companyId)
          .eq('platform', platformId)
          .eq('report_type', reportType);

        if (deleteError) throw deleteError;
      }

      // Update training progress
      const updateField = reportType === 'sales' ? 'sales_report_uploaded' :
                          reportType === 'payouts' ? 'payouts_report_uploaded' :
                          'quantities_report_uploaded';

      await supabase
        .from('pos_training_progress' as any)
        .update({
          [updateField]: false,
        })
        .eq('company_id', companyId)
        .eq('platform', platformId);

      // Update local state
      setPlatforms(prev => prev.map(p => {
        if (p.id === platformId) {
          return {
            ...p,
            salesUploaded: reportType === 'sales' ? false : p.salesUploaded,
            payoutsUploaded: reportType === 'payouts' ? false : p.payoutsUploaded,
            quantitiesUploaded: reportType === 'item_quantities' ? false : p.quantitiesUploaded,
          };
        }
        return p;
      }));

      toast({
        title: 'Report Removed',
        description: `${reportType === 'item_quantities' ? 'Item quantities' : reportType.charAt(0).toUpperCase() + reportType.slice(1)} report has been removed. You can now upload a new one.`,
      });
    } catch (error: any) {
      console.error('Error removing report:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove report',
        variant: 'destructive',
      });
    } finally {
      setRemoving(null);
    }
  };

  const getStatusBadge = (platform: Platform) => {
    const allUploaded = platform.salesUploaded && platform.payoutsUploaded && platform.quantitiesUploaded;
    const someUploaded = platform.salesUploaded || platform.payoutsUploaded || platform.quantitiesUploaded;
    
    if (allUploaded) {
      return <Badge className="bg-green-500 hover:bg-green-600">Complete</Badge>;
    } else if (someUploaded) {
      return <Badge variant="outline" className="border-amber-500 text-amber-600">In Progress</Badge>;
    }
    return <Badge variant="outline">Not Started</Badge>;
  };

  const getUploadProgress = (platform: Platform) => {
    const count = [platform.salesUploaded, platform.payoutsUploaded, platform.quantitiesUploaded].filter(Boolean).length;
    return `${count}/3 reports`;
  };

  const allPlatformsComplete = platforms.every(p =>
    p.salesUploaded && p.payoutsUploaded && p.quantitiesUploaded
  );

  const handleResetAll = async () => {
    try {
      setResetting(true);
      await resetCurrentUserPOSTrainingData(undefined, false);
      
      // Reload platforms to reflect the reset
      await loadPlatforms();
      
      toast({
        title: 'Success',
        description: 'All POS reports and mappings have been reset. You can now start fresh.',
      });
      
      setShowResetDialog(false);
    } catch (error: any) {
      console.error('Error resetting POS data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset POS data',
        variant: 'destructive',
      });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (platforms.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Upload POS Reports</h3>
          <p className="text-sm text-muted-foreground">
            Upload year-to-date reports for each of your POS platforms.
          </p>
        </div>
        <Card className="border-dashed">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                No POS platforms found. Please configure POS platforms in the previous step first.
              </p>
              {onPrevious && (
                <Button variant="outline" onClick={onPrevious}>
                  Go Back to Platform Setup
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-2">Upload POS Reports</h3>
          <p className="text-sm text-muted-foreground">
            Upload year-to-date reports for each of your POS platforms. Each platform requires 3 reports: Sales Transactions (or Sales and Payout combined), Payouts, and Item Quantities.
          </p>
        </div>
        {(platforms.some(p => p.salesUploaded || p.payoutsUploaded || p.quantitiesUploaded) || 
          platforms.some(p => p.status === 'mapping' || p.status === 'completed')) && (
          <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={resetting}
              >
                {resetting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset All Data
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset All POS Training Data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>All uploaded POS reports</li>
                    <li>All POS item mappings</li>
                    <li>All training progress</li>
                  </ul>
                  <p className="mt-2 font-medium text-destructive">
                    This action cannot be undone. You will need to re-upload all reports and re-map all items.
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleResetAll}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Reset All Data
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Accordion 
        type="multiple" 
        value={expandedPlatforms}
        onValueChange={setExpandedPlatforms}
        className="space-y-4"
      >
        {platforms.map((platform) => (
          <AccordionItem 
            key={platform.id} 
            value={platform.id}
            className="border rounded-lg overflow-hidden bg-card"
          >
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
              <div className="flex items-center gap-4 flex-1">
                <img
                  src={platform.logo}
                  alt={platform.name}
                  className="h-10 w-10 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div className="flex-1 text-left">
                  <div className="font-semibold">{platform.name}</div>
                  <div className="text-sm text-muted-foreground">{getUploadProgress(platform)}</div>
                </div>
                <div className="mr-2">
                  {getStatusBadge(platform)}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4 pt-2">
                {/* Sales Transactions Upload */}
                <Card className="border-muted">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">YTD Sales Transactions</CardTitle>
                        <CardDescription className="text-xs">
                          Upload your year-to-date sales transactions report (CSV or Excel)
                        </CardDescription>
                      </div>
                      {platform.salesUploaded && (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-3">
                    {platform.salesUploaded ? (
                      <div className="space-y-2">
                        <div className="border-2 border-green-200 rounded-lg p-4 text-center bg-green-50/50">
                          <CheckCircle2 className="h-6 w-6 mx-auto mb-1 text-green-600" />
                          <p className="text-sm font-medium text-green-700">Sales transactions uploaded</p>
                        </div>
                        <Button
                          onClick={() => handleRemoveReport(platform.id, 'sales')}
                          disabled={removing?.platformId === platform.id && removing?.reportType === 'sales'}
                          variant="ghost"
                          size="sm"
                          className="w-full text-muted-foreground hover:text-destructive"
                        >
                          {removing?.platformId === platform.id && removing?.reportType === 'sales' ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Removing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Replace with different file
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          id={`sales-upload-${platform.id}`}
                          className="hidden"
                          accept=".csv,.xlsx,.xls"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(platform.id, 'sales', file);
                            e.target.value = '';
                          }}
                          disabled={uploading?.platformId === platform.id && uploading?.reportType === 'sales'}
                        />
                        <label htmlFor={`sales-upload-${platform.id}`} className="cursor-pointer">
                          {uploading?.platformId === platform.id && uploading?.reportType === 'sales' ? (
                            <>
                              <Loader2 className="h-6 w-6 mx-auto mb-1 animate-spin" />
                              <p className="text-sm">Processing...</p>
                            </>
                          ) : (
                            <>
                              <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-sm">Click to upload CSV/Excel</p>
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Payouts Upload */}
                <Card className="border-muted">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">YTD Payout Report</CardTitle>
                        <CardDescription className="text-xs">
                          Upload your year-to-date payouts report
                        </CardDescription>
                      </div>
                      {platform.payoutsUploaded && (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-3">
                    {platform.payoutsUploaded ? (
                      <div className="space-y-2">
                        <div className="border-2 border-green-200 rounded-lg p-4 text-center bg-green-50/50">
                          <CheckCircle2 className="h-6 w-6 mx-auto mb-1 text-green-600" />
                          <p className="text-sm font-medium text-green-700">Payouts report uploaded</p>
                        </div>
                        <Button
                          onClick={() => handleRemoveReport(platform.id, 'payouts')}
                          disabled={removing?.platformId === platform.id && removing?.reportType === 'payouts'}
                          variant="ghost"
                          size="sm"
                          className="w-full text-muted-foreground hover:text-destructive"
                        >
                          {removing?.platformId === platform.id && removing?.reportType === 'payouts' ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Removing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Replace with different file
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          id={`payouts-upload-${platform.id}`}
                          className="hidden"
                          accept=".csv,.xlsx,.xls"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(platform.id, 'payouts', file);
                            e.target.value = '';
                          }}
                          disabled={uploading?.platformId === platform.id && uploading?.reportType === 'payouts'}
                        />
                        <label htmlFor={`payouts-upload-${platform.id}`} className="cursor-pointer">
                          {uploading?.platformId === platform.id && uploading?.reportType === 'payouts' ? (
                            <>
                              <Loader2 className="h-6 w-6 mx-auto mb-1 animate-spin" />
                              <p className="text-sm">Processing...</p>
                            </>
                          ) : (
                            <>
                              <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-sm">Click to upload CSV/Excel</p>
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Item Quantities Upload */}
                <Card className="border-muted">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">YTD Item Quantity Report</CardTitle>
                        <CardDescription className="text-xs">
                          Upload your year-to-date item quantities report
                        </CardDescription>
                      </div>
                      {platform.quantitiesUploaded && (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-3">
                    {platform.quantitiesUploaded ? (
                      <div className="space-y-2">
                        <div className="border-2 border-green-200 rounded-lg p-4 text-center bg-green-50/50">
                          <CheckCircle2 className="h-6 w-6 mx-auto mb-1 text-green-600" />
                          <p className="text-sm font-medium text-green-700">Quantities report uploaded</p>
                        </div>
                        <Button
                          onClick={() => handleRemoveReport(platform.id, 'item_quantities')}
                          disabled={removing?.platformId === platform.id && removing?.reportType === 'item_quantities'}
                          variant="ghost"
                          size="sm"
                          className="w-full text-muted-foreground hover:text-destructive"
                        >
                          {removing?.platformId === platform.id && removing?.reportType === 'item_quantities' ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Removing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Replace with different file
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          id={`quantities-upload-${platform.id}`}
                          className="hidden"
                          accept=".csv,.xlsx,.xls"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(platform.id, 'item_quantities', file);
                            e.target.value = '';
                          }}
                          disabled={uploading?.platformId === platform.id && uploading?.reportType === 'item_quantities'}
                        />
                        <label htmlFor={`quantities-upload-${platform.id}`} className="cursor-pointer">
                          {uploading?.platformId === platform.id && uploading?.reportType === 'item_quantities' ? (
                            <>
                              <Loader2 className="h-6 w-6 mx-auto mb-1 animate-spin" />
                              <p className="text-sm">Processing...</p>
                            </>
                          ) : (
                            <>
                              <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-sm">Click to upload CSV/Excel</p>
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="flex justify-between pt-4">
        {onPrevious && (
          <Button variant="outline" onClick={onPrevious}>
            Previous Step
          </Button>
        )}
        <Button 
          onClick={onNext} 
          disabled={!allPlatformsComplete}
          className={!onPrevious ? 'ml-auto' : ''}
        >
          {allPlatformsComplete ? (
            <>
              Continue to Mapping
              <ChevronRight className="h-4 w-4 ml-1" />
            </>
          ) : (
            'Complete all uploads to continue'
          )}
        </Button>
      </div>
    </div>
  );
};
